import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowLeft, ArrowUpRight, BarChart3, ChevronLeft, ChevronRight, Clock3, Flag, Gauge, IndianRupee, Loader2, Package, PhoneCall, PhoneOutgoing, Target, Trophy, Users, Waypoints } from "lucide-react";
import { dateLocale } from "@/lib/dateTime";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BarList, EmptyState, ProgressBar, ReportSkeleton, SectionCard, StatTile, TONE, achievementTone, fmtMoney, fmtMonth, fmtNum, fmtPct, type Tone } from "@/components/reports/ReportWidgets";
import { buildQuery, useReport } from "@/components/reports/DateRangeFilter";
import type { LeadBuckets, OverviewRevenueRow, SalesOverview } from "@/types/report";

type View = "month" | "year";

const ym = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const ymd = (d: Date) => `${ym(d)}-${String(d.getDate()).padStart(2, "0")}`;
const thisMonth = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1);
const dur = (s: number) => (s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`);

/** Lead buckets: open leads in the Follow-up stage, the rest by their Hot / Warm / Cold priority */
const BUCKETS: { key: keyof Omit<LeadBuckets, "total">; label: string; tone: Tone; note: string }[] = [
  { key: "cold", label: "Cold", tone: "blue", note: "Only exploring" },
  { key: "follow_up", label: "Follow-up", tone: "purple", note: "In the Follow-up stage" },
  { key: "warm", label: "Warm", tone: "orange", note: "Interested" },
  { key: "hot", label: "Hot", tone: "red", note: "Ready to buy" },
];

/** Up/down against the comparison period, in words as well as an arrow */
function Change({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-muted-foreground">—</span>;
  const up = pct >= 0;
  return (
    <span className={cn("inline-flex items-center gap-0.5 font-medium", up ? "text-success" : "text-danger")}>
      {up ? <ArrowUpRight className="size-3.5" aria-hidden /> : <ArrowDownRight className="size-3.5" aria-hidden />}
      {up ? "+" : ""}
      {pct.toFixed(Math.abs(pct) < 10 ? 1 : 0)}%
    </span>
  );
}

const Meter = ProgressBar;

/** A section heading with a coloured icon */
function SectionTitle({ id, icon, tone, children }: { id: string; icon: React.ReactNode; tone: Tone; children: React.ReactNode }) {
  return (
    <h3 id={id} className="flex items-center gap-2 text-[15px] font-semibold">
      <span className={cn("flex size-7 items-center justify-center rounded-lg [&_svg]:size-4", TONE[tone].chip)}>{icon}</span>
      {children}
    </h3>
  );
}

function SegmentToggle<T extends string | number>({ label, value, options, onChange }: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div role="group" aria-label={label} className="flex overflow-hidden rounded-lg border border-border">
      {options.map(([k, text]) => (
        <button
          key={String(k)}
          type="button"
          aria-pressed={value === k}
          onClick={() => onChange(k)}
          className={cn("h-[30px] px-3 text-xs", value === k ? "bg-sidebar font-semibold text-white" : "bg-card text-muted-foreground hover:text-foreground")}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/** Monthly won value as columns, with each month's target as a tick across its column */
function TrendChart({ data, height = 170 }: { data: SalesOverview["revenue"]["trend"]; height?: number }) {
  const max = Math.max(0, ...data.map((d) => Math.max(d.won_amount, d.target_amount)));
  if (!max) return <EmptyState message="Nothing won in these months yet." />;
  const hasTarget = data.some((d) => d.target_amount > 0);
  const room = height - 22;
  return (
    <div>
      <div className="flex items-end gap-1 border-b border-border" style={{ height }}>
        {data.map((d) => {
          const label = `${fmtMonth(d.period)}: ${fmtMoney(d.won_amount)} won${d.target_amount ? ` of ${fmtMoney(d.target_amount)} target (${Math.round((d.won_amount / d.target_amount) * 100)}%)` : ""}`;
          return (
            <div key={d.period} tabIndex={0} title={label} aria-label={label} className="group relative flex h-full flex-1 flex-col items-center justify-end rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {d.won_amount > 0 && <span className="mb-0.5 whitespace-nowrap text-[10px] font-semibold tabular-nums text-foreground">{fmtMoney(d.won_amount)}</span>}
              <div
                className="w-full max-w-[34px] rounded-t-[4px] transition-colors group-hover:[background:var(--viz-series-1-hover)]"
                style={{ height: Math.max(d.won_amount > 0 ? 2 : 0, (d.won_amount / max) * room), background: "var(--viz-series-1)" }}
              />
              {d.target_amount > 0 && <div className="absolute h-0.5 w-full max-w-[46px] bg-foreground" style={{ bottom: (d.target_amount / max) * room }} />}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-1">
        {data.map((d) => (
          <span key={d.period} className="flex-1 truncate text-center text-[10px] text-muted-foreground">
            {fmtMonth(d.period)}
          </span>
        ))}
      </div>
      {hasTarget && (
        <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-sm" style={{ background: "var(--viz-series-1)" }} />Revenue won</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-0.5 w-3 bg-foreground" />Target</span>
        </div>
      )}
    </div>
  );
}

export function OverviewTab({
  headId,
  userId,
  onPickUser,
  onPickHead,
  onBack,
  onParams,
}: {
  headId: string;
  userId: string;
  /** The month / year / day / trend shown, so the PDF and Excel match the screen */
  onParams?: (p: Record<string, string | number>) => void;
  onPickUser: (id: string) => void;
  /** Super Admin only: a head's row opens their team */
  onPickHead?: (id: string) => void;
  onBack: () => void;
}) {
  const [view, setView] = useState<View>("month");
  const [anchor, setAnchor] = useState(thisMonth);
  const [day, setDay] = useState(() => ymd(new Date()));
  const [trend, setTrend] = useState(6);

  const url = `/api/Report/Overview${buildQuery({ view, period: ym(anchor), day, trend, head_id: headId, user_id: userId })}`;
  useEffect(() => {
    onParams?.({ view, period: ym(anchor), day, trend });
  }, [onParams, view, anchor, day, trend]);
  const { data, loading, error } = useReport<SalesOverview>(url);

  const month = view === "month";
  const shift = (by: number) => setAnchor((d) => new Date(d.getFullYear(), d.getMonth() + (month ? by : by * 12), 1));
  const title = month ? anchor.toLocaleDateString(dateLocale(), { month: "long", year: "numeric" }) : String(anchor.getFullYear());
  const isCurrent = month ? ym(anchor) === ym(new Date()) : anchor.getFullYear() === new Date().getFullYear();
  const today = ymd(new Date());

  const team = !!data && data.people > 1;
  const name = data?.person?.name;
  const compareLabel = !data
    ? ""
    : data.partial
      ? month ? "vs same days last month" : "vs same period last year"
      : month
        ? `vs ${new Date(data.compare.from).toLocaleDateString(dateLocale(), { month: "short" })}`
        : `vs ${new Date(data.compare.from).getFullYear()}`;

  const pick = (r: { user_id: string; role_code?: string }) => {
    if (r.role_code === "SALES_HEAD") onPickHead?.(r.user_id);
    else onPickUser(r.user_id);
  };
  const canPick = (r: { role_code?: string }) => team && (r.role_code !== "SALES_HEAD" || !!onPickHead);

  return (
    <div className="space-y-4">
      {/* Period controls */}
      <div className="flex flex-wrap items-center gap-2.5">
        {userId && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="size-4" />
            Back to team
          </Button>
        )}
        <h2 className="text-[19px] font-semibold tracking-tight">
          {name && !team ? `${name} · ` : ""}
          {title}
        </h2>
        <Button variant="outline" size="icon-sm" aria-label={month ? "Previous month" : "Previous year"} onClick={() => shift(-1)}>
          <ChevronLeft className="size-4" />
        </Button>
        <Button variant="outline" size="icon-sm" aria-label={month ? "Next month" : "Next year"} onClick={() => shift(1)}>
          <ChevronRight className="size-4" />
        </Button>
        {!isCurrent && (
          <Button variant="ghost" size="sm" onClick={() => setAnchor(thisMonth())}>
            This {view}
          </Button>
        )}
        {loading && <Loader2 className="size-4 animate-spin text-primary" aria-label="Loading" />}
        <div className="flex-1" />
        {userId && data?.person && (
          <Link to={`/reports/user/${data.person.id}`} className="text-xs font-medium text-primary hover:underline">
            Full profile →
          </Link>
        )}
        <SegmentToggle<View> label="Monthly or yearly" value={view} options={[["month", "Monthly"], ["year", "Yearly"]]} onChange={setView} />
      </div>

      {error && <p className="py-6 text-center text-sm text-destructive">{error}</p>}
      {!data && !error && <ReportSkeleton tiles={4} cards={2} />}

      {data && (
        <div className={cn("space-y-4 transition-opacity", loading && "opacity-60")}>
          <LeadSection data={data} team={team} pick={(id) => onPickUser(id)} />
          <CallSection data={data} team={team} day={day} today={today} setDay={setDay} pick={(id) => onPickUser(id)} />
          <RevenueSection data={data} month={month} team={team} compareLabel={compareLabel} trend={trend} setTrend={setTrend} pick={pick} canPick={canPick} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenue
// ---------------------------------------------------------------------------

function RevenueSection({
  data,
  month,
  team,
  compareLabel,
  trend,
  setTrend,
  pick,
  canPick,
}: {
  data: SalesOverview;
  month: boolean;
  team: boolean;
  compareLabel: string;
  trend: number;
  setTrend: (n: number) => void;
  pick: (r: OverviewRevenueRow) => void;
  canPick: (r: OverviewRevenueRow) => boolean;
}) {
  const t = data.revenue.totals;
  const running = data.elapsed_pct > 0 && data.elapsed_pct < 100;
  const perDay = t.remaining && data.days_left ? t.remaining / data.days_left : 0;
  const periodWord = month ? "month" : "year";

  return (
    <section aria-labelledby="ov-revenue" className="space-y-3">
      <SectionTitle id="ov-revenue" icon={<IndianRupee />} tone="green">
        {team ? "Revenue" : "My revenue"} · {month ? "monthly view" : "yearly view"}
      </SectionTitle>
      <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
        <StatTile
          label={team ? "Total revenue" : "My revenue"}
          value={fmtMoney(t.won_amount)}
          rawValue={t.won_amount}
          previous={t.prev_won_amount}
          previousLabel={compareLabel}
          tone={t.change_pct === null ? "blue" : t.change_pct >= 0 ? "green" : "red"}
          icon={<IndianRupee />}
          hint={<span>· {t.deals_won} deal{t.deals_won === 1 ? "" : "s"} won</span>}
        />
        <StatTile
          label={`${month ? "Monthly" : "Yearly"} target`}
          tone="purple"
          icon={<Target />}
          value={t.target_amount ? fmtMoney(t.target_amount) : "—"}
          hint={<span>{t.target_amount ? (month ? "Set on the Targets page" : "The 12 monthly targets added up") : "No target set"}</span>}
        />
        <StatTile
          label="Achievement"
          value={fmtPct(t.achieved_pct)}
          tone={achievementTone(t.achieved_pct)}
          icon={<Gauge />}
          hint={t.achieved_pct !== null && running ? <span>{Math.round(data.elapsed_pct)}% of the {periodWord} has gone</span> : undefined}
        />
        <StatTile
          label="Remaining target"
          tone={t.remaining === 0 ? "green" : t.remaining === null ? "gray" : "orange"}
          icon={<Flag />}
          value={t.remaining === null ? "—" : t.remaining === 0 ? "Met" : fmtMoney(t.remaining)}
          hint={
            t.remaining === 0 && t.target_amount ? (
              <span>{fmtMoney(t.won_amount - t.target_amount)} over target</span>
            ) : perDay > 0 && running ? (
              <span>
                {fmtMoney(perDay)} a day for {data.days_left} day{data.days_left === 1 ? "" : "s"}
              </span>
            ) : undefined
          }
        />
      </div>

      <div className="grid gap-3 @4xl/main:grid-cols-5">
        {team && (
          <SectionCard
            title="Revenue by salesperson"
            icon={<Trophy />}
            tone="orange"
            description={`Ranked by revenue won. ${compareLabel.replace("vs", "Change vs")}.`}
            className="@4xl/main:col-span-3"
          >
            <Leaderboard rows={data.revenue.rows} pick={pick} canPick={canPick} />
          </SectionCard>
        )}
        <SectionCard
          title={month ? "Revenue trend" : "Month by month"}
          icon={<BarChart3 />}
          description={month ? `The last ${trend} months up to the month shown` : `Each month of ${new Date(data.from).getFullYear()}`}
          className={team ? "@4xl/main:col-span-2" : "@4xl/main:col-span-5"}
          action={month ? <SegmentToggle<number> label="Months in the trend" value={trend} options={[[3, "3m"], [6, "6m"], [12, "12m"]]} onChange={setTrend} /> : undefined}
        >
          <TrendChart data={data.revenue.trend} />
        </SectionCard>
      </div>

      <div className="grid gap-3 @4xl/main:grid-cols-2">
        <SectionCard title="Revenue by source" description="Where the won deals' leads came from" icon={<Waypoints />} tone="green">
          <BarList tone="green" data={data.revenue.by_source.map((s) => ({ label: s.label, value: s.amount, detail: [`${s.deals} deal${s.deals === 1 ? "" : "s"}`] }))} format={(v) => fmtMoney(v)} emptyMessage="No revenue in this period." />
        </SectionCard>
        <SectionCard title="Revenue by product" description="Line totals of the won deals" icon={<Package />} tone="purple">
          <BarList tone="purple" data={data.revenue.by_product.map((s) => ({ label: s.label, value: s.amount, detail: [`${s.deals} deal${s.deals === 1 ? "" : "s"}`] }))} format={(v) => fmtMoney(v)} emptyMessage="No won deals with products in this period." />
        </SectionCard>
      </div>
    </section>
  );
}

function Leaderboard({ rows, pick, canPick }: { rows: OverviewRevenueRow[]; pick: (r: OverviewRevenueRow) => void; canPick: (r: OverviewRevenueRow) => boolean }) {
  if (!rows.length) return <EmptyState message="Nobody to rank in this view." />;
  return (
    <div className="-mx-5 overflow-x-auto px-5">
      <table className="w-full min-w-[560px] text-xs">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground">
            <th className="w-8 px-1.5 py-2 font-semibold">#</th>
            <th className="px-1.5 py-2 font-semibold">Salesperson</th>
            <th className="px-1.5 py-2 text-right font-semibold">Revenue</th>
            <th className="w-[150px] px-1.5 py-2 font-semibold">Of target</th>
            <th className="px-1.5 py-2 text-right font-semibold">Remaining</th>
            <th className="px-1.5 py-2 text-right font-semibold">Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const clickable = canPick(r);
            return (
              <tr
                key={r.user_id}
                className={cn("border-b border-border/60 last:border-0", clickable && "cursor-pointer hover:bg-accent/60")}
                onClick={clickable ? () => pick(r) : undefined}
              >
                <td className="px-1.5 py-2">
                  <span className={cn("flex size-6 items-center justify-center rounded-full font-mono text-[11px] font-bold", r.rank === 1 ? "bg-gold-soft text-gold-ink" : "bg-muted text-muted-foreground")}>{r.rank}</span>
                </td>
                <td className="px-1.5 py-2">
                  {clickable ? (
                    <button type="button" className="font-medium text-foreground hover:text-primary hover:underline" onClick={(e) => (e.stopPropagation(), pick(r))}>
                      {r.name}
                    </button>
                  ) : (
                    <span className="font-medium text-foreground">{r.name}</span>
                  )}
                  {r.role_code === "SALES_HEAD" && <span className="ml-1.5 text-[11px] text-muted-foreground">Sales Head · own sales</span>}
                </td>
                <td className="px-1.5 py-2 text-right font-mono font-semibold tabular-nums">
                  {fmtMoney(r.won_amount)}
                  <span className="block text-[11px] font-normal text-muted-foreground">{r.deals_won} deal{r.deals_won === 1 ? "" : "s"}</span>
                </td>
                <td className="px-1.5 py-2">
                  {r.target_amount === null ? <span className="text-muted-foreground">Team target</span> : <Meter pct={r.target_amount ? r.achieved_pct : null} />}
                  {!!r.target_amount && <span className="block text-[11px] text-muted-foreground">of {fmtMoney(r.target_amount)}</span>}
                </td>
                <td className="px-1.5 py-2 text-right font-mono tabular-nums">{r.remaining === null ? "—" : r.remaining === 0 ? <span className="font-semibold text-success">Met</span> : <span className="text-warning">{fmtMoney(r.remaining)}</span>}</td>
                <td className="px-1.5 py-2 text-right">
                  <Change pct={r.change_pct} />
                  <span className="block text-[11px] text-muted-foreground">was {fmtMoney(r.prev_won_amount)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lead status
// ---------------------------------------------------------------------------

function LeadSection({ data, team, pick }: { data: SalesOverview; team: boolean; pick: (id: string) => void }) {
  const t = data.leads.totals;
  const rows = data.leads.rows;
  const max = Math.max(0, ...rows.map((r) => r.total));
  return (
    <section aria-labelledby="ov-leads" className="space-y-3">
      <SectionTitle id="ov-leads" icon={<Users />} tone="blue">
        {team ? "Lead status" : "My lead status"} <span className="font-normal text-muted-foreground">· open leads as of now · {fmtNum(t.total)} in all</span>
      </SectionTitle>
      <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
        {BUCKETS.map((b) => (
          <div key={b.key} className={cn("relative flex flex-col gap-1 overflow-hidden rounded-xl border border-border px-4 py-3 shadow-xs", TONE[b.tone].soft)}>
            <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1", TONE[b.tone].bar)} />
            <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span className={cn("size-2.5 rounded-full", TONE[b.tone].bar)} aria-hidden />
              {team ? b.label : `My ${b.label.toLowerCase()}`} leads
            </span>
            <span className={cn("text-2xl font-bold tabular-nums", TONE[b.tone].text)}>{fmtNum(t[b.key])}</span>
            <span className="text-xs text-muted-foreground">
              {t.total ? `${Math.round((t[b.key] / t.total) * 100)}% · ` : ""}
              {b.note}
            </span>
          </div>
        ))}
      </div>
      {team && (
        <SectionCard title="By salesperson" description="Each bar is that person's open leads, split by status. Click a name for their report." icon={<Users />}>
          {rows.length === 0 ? (
            <EmptyState message="No open leads." />
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {BUCKETS.map((b) => (
                  <span key={b.key} className="inline-flex items-center gap-1.5">
                    <span className={cn("size-2.5 rounded-sm", TONE[b.tone].bar)} />
                    {b.label}
                  </span>
                ))}
              </div>
              {rows.map((r) => (
                <div key={r.user_id} className="grid grid-cols-[minmax(90px,22%)_1fr_auto] items-center gap-3">
                  <button type="button" className="truncate text-left text-xs text-foreground hover:text-primary hover:underline" title={r.name} onClick={() => pick(r.user_id)}>
                    {r.name}
                  </button>
                  <div
                    className="flex h-4 overflow-hidden rounded-r-[4px]"
                    style={{ width: `${Math.max(2, (r.total / max) * 100)}%` }}
                    role="img"
                    aria-label={`${r.name}: ${BUCKETS.map((b) => `${r[b.key]} ${b.label.toLowerCase()}`).join(", ")}`}
                  >
                    {BUCKETS.map((b) => (r[b.key] ? <div key={b.key} className={TONE[b.tone].bar} style={{ width: `${(r[b.key] / r.total) * 100}%` }} title={`${b.label}: ${r[b.key]}`} /> : null))}
                  </div>
                  <span className="w-28 text-right text-[11px] tabular-nums text-muted-foreground">
                    {BUCKETS.map((b) => r[b.key]).join(" · ")} = <strong className="text-foreground">{r.total}</strong>
                  </span>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Daily calls
// ---------------------------------------------------------------------------

function CallSection({ data, team, day, today, setDay, pick }: { data: SalesOverview; team: boolean; day: string; today: string; setDay: (d: string) => void; pick: (id: string) => void }) {
  const c = data.calls.totals;
  const isToday = day === today;
  const dayLabel = isToday ? "today" : new Date(`${day}T00:00:00`).toLocaleDateString(dateLocale(), { weekday: "short", day: "2-digit", month: "short" });
  return (
    <section aria-labelledby="ov-calls" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <SectionTitle id="ov-calls" icon={<PhoneCall />} tone="purple">
          {team ? "Daily call count" : "My daily calls"} · {dayLabel}
        </SectionTitle>
        <div className="flex-1" />
        {!isToday && (
          <Button variant="ghost" size="sm" onClick={() => setDay(today)}>
            Today
          </Button>
        )}
        <Input type="date" aria-label="Day" className="h-8 w-[150px] text-xs" value={day} max={today} onChange={(e) => e.target.value && setDay(e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3 @4xl/main:grid-cols-4">
        <StatTile
          label={team ? "Calls made by the team" : "Calls made"}
          value={fmtNum(c.attempts)}
          icon={<PhoneOutgoing />}
          hint={c.target_daily_calls ? <span>Target {fmtNum(c.target_daily_calls)}</span> : <span>No daily target set</span>}
        />
        <StatTile label="Target achieved" value={fmtPct(c.achieved_pct)} tone={achievementTone(c.achieved_pct)} icon={<Target />} hint={c.target_daily_calls ? <Meter pct={c.achieved_pct} className="w-full" /> : <span>Set it on Targets → Calls a day</span>} />
        <StatTile label="Connected" value={`${fmtNum(c.connected)} of ${fmtNum(c.attempts)}`} tone="green" icon={<PhoneCall />} hint={<span>{fmtPct(c.contact_rate_pct)} connected · talked {data.calls.connected_min_sec}+ s</span>} />
        <StatTile label="Average call" value={c.connected ? dur(c.avg_talk_sec) : "—"} tone="purple" icon={<Clock3 />} hint={<span>Talk time on connected calls</span>} />
      </div>
      {team && (
        <SectionCard title="Calls by salesperson" description="Calls made against each person's daily target. Click a name for their report." icon={<Users />} tone="purple">
          {data.calls.rows.length === 0 ? (
            <EmptyState message={`No calls ${isToday ? "yet today" : "on this day"}.`} />
          ) : (
            <div className="-mx-5 overflow-x-auto px-5">
              <table className="w-full min-w-[560px] text-xs">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="px-1.5 py-2 font-semibold">Salesperson</th>
                    <th className="px-1.5 py-2 text-right font-semibold">Calls</th>
                    <th className="px-1.5 py-2 text-right font-semibold">Connected</th>
                    <th className="px-1.5 py-2 text-right font-semibold">Connect %</th>
                    <th className="px-1.5 py-2 text-right font-semibold">Avg call</th>
                    <th className="w-[170px] px-1.5 py-2 font-semibold">Daily target</th>
                  </tr>
                </thead>
                <tbody>
                  {data.calls.rows.map((r) => (
                    <tr key={r.user_id} className="border-b border-border/60 last:border-0">
                      <td className="px-1.5 py-2">
                        <button type="button" className="font-medium text-foreground hover:text-primary hover:underline" onClick={() => pick(r.user_id)}>
                          {r.name}
                        </button>
                      </td>
                      <td className="px-1.5 py-2 text-right font-mono font-semibold tabular-nums">{r.attempts}</td>
                      <td className="px-1.5 py-2 text-right font-mono tabular-nums">{r.connected}</td>
                      <td className="px-1.5 py-2 text-right font-mono tabular-nums">{fmtPct(r.contact_rate_pct)}</td>
                      <td className="px-1.5 py-2 text-right font-mono tabular-nums">{r.connected ? dur(r.avg_talk_sec) : "—"}</td>
                      <td className="px-1.5 py-2">
                        <Meter pct={r.target_daily_calls ? r.achieved_pct : null} />
                        {r.target_daily_calls > 0 && <span className="block text-[11px] text-muted-foreground">{r.attempts} of {r.target_daily_calls}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}
    </section>
  );
}
